"use client";

import Link from "next/link";
import { CircleAlert, Gavel, Plus, SearchX } from "lucide-react";
import { SearchField } from "@/features/home-v2/browse/BrowseControls";
import DateField from "@/features/home-v2/browse/DateField";
import SortMenu from "@/features/home-v2/browse/SortMenu";
import { DeskBar, DeskCover, DeskEmpty, DeskMore } from "@/features/home-v2/desk/Desk";
import { RouteHero, RouteHeroMark } from "@/features/home-v2/RouteHero";
import { ActionButton } from "@/features/home-v2/form/Action";
import { Dialog, Loading } from "@/features/home-v2/ui";
import { useScrollReveal } from "@/features/home-v2/useScrollReveal";
import { useSharedNow } from "@/lib/countdown";
import { useMounted } from "../single/designs/parts";
import MyAuctionEntry from "./MyAuctionEntry";
import { useMyAuctions } from "./useMyAuctions";
import { useOwnedAuctionActions } from "./useOwnedAuctionActions";
import "./my-auctions.css";
import { useGridColumns } from "@/hooks/useGridColumns";

/**
 * «مزایده‌های من» — the owner's auctions, in v2: on its own route
 * (`/auctions`) under a route hero, and inside the profile's owner tab
 * (`embedded`) under its own ledger cover. Every rule is the legacy
 * MyAuctionsClient's (`useMyAuctions`) and AuctionListActions'
 * (`useOwnedAuctionActions`).
 */
export default function MyAuctions({
  lang,
  embedded = false
}: {
  lang: "fa" | "en";
  embedded?: boolean;
}) {
  const isFa = lang === "fa";
  const t = (fa: string, en: string) => isFa ? fa : en;
  const nf = new Intl.NumberFormat(isFa ? "fa-IR" : "en-US");
  const m = useMyAuctions(lang);
  const f = m.filters;
  const acts = useOwnedAuctionActions({
    lang,
    onChanged: m.refresh
  });
  const mounted = useMounted();
  // A minute is fine enough for a bar that spans days.
  const nowMs = useSharedNow(mounted && m.auctions.some(a => a.status === "active" || a.status === "draft"), 60_000);
  const grid = useGridColumns();
  const reveal = useScrollReveal({
    columns: grid.columns,
    loaded: m.auctions.length,
    canFetchMore: m.canFetchMore,
    fetchMore: m.fetchMore,
    isFetching: m.isFetchingMore,
    resetKey: JSON.stringify([f.title, f.modelType, f.currencyId, f.fromDate, f.toDate])
  });
  const c = m.statusCounts;
  const loaded = t("از موارد بارگذاری‌شده", "of those loaded");
  const tally = [{
    key: "all",
    label: t("همه‌ی مزایده‌ها", "All auctions"),
    value: nf.format(m.total),
    tone: "accent" as const
  }, {
    key: "draft",
    label: t("پیش‌نویس", "Drafts"),
    value: nf.format(c.draft ?? 0),
    tone: "muted" as const,
    note: loaded
  }, {
    key: "active",
    label: t("فعال", "Active"),
    value: nf.format(c.active ?? 0),
    tone: "live" as const,
    note: loaded
  }, {
    key: "closed",
    label: t("تمام‌شده یا لغوشده", "Finished or canceled"),
    value: nf.format((c.finished ?? 0) + (c.canceled ?? 0)),
    tone: "gold" as const,
    note: loaded
  }];
  const create = {
    href: `/${lang}/auctions/create`,
    label: t("ساخت مزایده‌ی تازه", "New auction")
  };
  const isDelete = acts.pending?.action === "delete";
  let body: React.ReactNode;
  if (m.isLoading) body = <Loading label={t("در حال بارگذاری مزایده‌ها", "Loading your auctions")} />;else if (m.isError) body = <DeskEmpty tone="error" icon={<CircleAlert />} title={t("مزایده‌ها بارگذاری نشد", "Your auctions didn't load")} body={t("ارتباط با سرور برقرار نشد. چند لحظه بعد دوباره تلاش کنید.", "We couldn't reach the server. Try again in a moment.")} actions={<button type="button" className="v2-btn v2-btn--ghost v2-focus" onClick={m.retry}>{t("تلاش دوباره", "Try again")}</button>} />;else if (m.auctions.length === 0) {
    body = f.filtered ? <DeskEmpty tone="filter" icon={<SearchX />} title={t("مزایده‌ای با این فیلترها پیدا نشد", "No auctions match these filters")} body={t("عنوان، نوع، ارز یا بازه‌ی تاریخ را تغییر دهید، یا همه‌ی فیلترها را پاک کنید.", "Change the title, kind, currency or dates — or clear every filter.")} actions={<button type="button" className="v2-btn v2-btn--ghost v2-focus" onClick={f.reset}>{t("پاک کردن فیلترها", "Clear filters")}</button>} /> : <DeskEmpty icon={<Gavel />} title={t("هنوز مزایده‌ای نساخته‌اید", "You haven't created an auction yet")} body={t("یک اثر یا کالکشن را به مزایده بگذارید؛ این دفتر وضعیت، زمان و پیشنهادهای هرکدام را برایتان نگه می‌دارد.", "Put a piece or a collection up for auction — this ledger keeps each one's state, time and bids for you.")} actions={<><Link className="v2-btn v2-focus" href={create.href}><Plus aria-hidden />{create.label}</Link><Link className="v2-btn v2-btn--ghost v2-focus" href={`/${lang}/active-bid`}>{t("دیدن مزایده‌های دیگران", "See other auctions")}</Link></>} />;
  } else {
    body = <>
        <ol ref={grid.ref} className="ma-ledger" aria-busy={m.isRefreshing || undefined}>
          {m.auctions.slice(0, reveal.count).map(a_0 => <li key={a_0.id}><MyAuctionEntry a={a_0} lang={lang} nowMs={nowMs} mounted={mounted} onAsk={acts.ask} /></li>)}
        </ol>
        <DeskMore show={reveal.hasMore} busy={m.isFetchingMore} onMore={reveal.more} sentinel={reveal.sentinel} label={t("مزایده‌های بیشتر", "More auctions")} />
      </>;
  }
  return <div className={embedded ? "dk dk--tab ma" : "v2-page dk ma"}>
      {embedded ? null : <RouteHero eyebrow={<><Gavel aria-hidden />{t("میز کار", "Your desk")}</>} title={t("مزایده‌های من", "My auctions")} description={t("همه‌ی مزایده‌هایی که ساخته‌اید، با وضعیت، زمان و کارهایی که روی هرکدام می‌شود کرد.", "Every auction you've created — its state, its time, and what you can do with it.")} aside={<RouteHeroMark icon={<Gavel />} value={nf.format(m.total)} caption={t("مزایده", "auctions")} />} />}

      <DeskCover title={embedded ? t("مزایده‌های من", "My auctions") : undefined} lead={embedded ? t("هر مزایده با وضعیت، زمان و کارهایی که روی آن می‌شود کرد.", "Each auction with its state, its time and what you can do with it.") : undefined} tally={tally} create={create} />

      <DeskBar label={t("جست‌وجو و فیلتر مزایده‌ها", "Search and filter your auctions")} onReset={f.reset} resetLabel={t("پاک کردن فیلترها", "Clear filters")} canReset={f.filtered}>
        <SearchField id={embedded ? "ma-search-tab" : "ma-search"} value={f.title} onChange={f.setTitle} placeholder={t("عنوان مزایده", "Auction title")} label={t("جست‌وجو در عنوان مزایده‌ها", "Search auction titles")} clearLabel={t("پاک کردن", "Clear")} />
        <SortMenu value={f.modelType || "all"} label={t("نوع", "Kind")} align="start" onChange={v => f.setModelType(v === "all" ? "" : v)} options={[{
        key: "all",
        label: t("همه‌ی انواع", "All kinds")
      }, {
        key: "nft",
        label: "NFT"
      }, {
        key: "collection",
        label: t("کالکشن", "Collection")
      }]} />
        <SortMenu value={f.currencyId || "all"} label={t("ارز", "Currency")} onChange={v_0 => f.setCurrencyId(v_0 === "all" ? "" : v_0)} options={[{
        key: "all",
        label: t("همه‌ی ارزها", "All currencies")
      }, ...m.currencies]} />
        <DateField label={t("از", "From")} value={f.fromDate} onChange={f.setFromDate} lang={lang} />
        <DateField label={t("تا", "To")} value={f.toDate} onChange={f.setToDate} lang={lang} edge="end" />
      </DeskBar>

      {body}

      <Dialog open={Boolean(acts.pending)} onClose={acts.close} title={isDelete ? t("حذف مزایده", "Delete the auction") : t("لغو مزایده", "Cancel the auction")} closeLabel={t("بستن", "Close")} className="ma-dialog" busy={acts.isSubmitting} footer={<div className="ma-dlg-foot">
            <button type="button" className="v2-btn v2-btn--ghost v2-focus" onClick={acts.close} disabled={acts.isSubmitting}>{t("انصراف", "Cancel")}</button>
            <ActionButton className={isDelete ? "ma-danger-solid" : undefined} onClick={() => void acts.confirm()} busy={acts.isSubmitting} busyLabel={isDelete ? t("در حال حذف…", "Deleting…") : t("در حال لغو…", "Canceling…")}>
              {isDelete ? t("حذف مزایده", "Delete auction") : t("لغو مزایده", "Cancel auction")}
            </ActionButton>
          </div>}>
        <p className="ma-dlg-lead"><b>{acts.pending?.auction.title}</b></p>
        <p className="ma-dlg-lead">{isDelete ? t("مزایده حذف می‌شود و قابل برگشت نیست.", "The auction is deleted. This can't be undone.") : t("مزایده لغو می‌شود و از چرخه‌ی فعال بیرون می‌رود.", "The auction is canceled and leaves the active flow.")}</p>
      </Dialog>
    </div>;
}